/**
 * Entrar: e-mail e senha, ou Google.
 *
 * Substituiu o código por e-mail como porta principal. Pedir que a pessoa
 * saia do app, abra o e-mail e volte com um número perdia gente no meio --
 * e quem já entrou uma vez não quer repetir isso todo dia.
 *
 * O código continua existindo em "esqueci minha senha" (CodigoPorEmail): é a
 * única saída de quem não lembra a senha e não usa Google.
 */
import { ApiError } from '@lindeza/core';
import { useRuntime } from '@lindeza/core/hooks';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconChevronLeft, IconMail } from '../../components/Icons';
import { Brand, EntrarShell, useDesktop } from './EntrarShell';
import { GoogleButton, googleEnabled } from './GoogleButton';
import './entrar.css';

export function Login() {
  const rt = useRuntime();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function entrar(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Confira o e-mail.');
      return;
    }
    if (!senha) {
      setError('Digite sua senha.');
      return;
    }
    setBusy(true);
    try {
      await rt.loginWithPassword(email, senha);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.isNetwork
          ? 'Sem internet. Para entrar, conecte-se uma vez.'
          : err instanceof ApiError && err.status === 422
            ? // A API devolve a mesma mensagem para e-mail errado e senha errada,
              // de propósito: dizer qual dos dois entregaria quem é cliente.
              'E-mail ou senha não conferem.'
            : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  const H = desktop ? 'h2' : 'h1';

  const form = (
    <form className="en-form" onSubmit={entrar} noValidate>
      {!desktop && (
        <header className="en-bar">
          <button type="button" className="en-back" onClick={() => navigate('/')}>
            <IconChevronLeft strokeWidth={2.5} /> Voltar
          </button>
        </header>
      )}

      <div className={desktop ? undefined : 'en-pad'}>
        <H className={desktop ? 'en-dk-h' : 'en-h'}>Entrar</H>
        <p className={desktop ? 'en-dk-sub' : 'en-sub'}>Use o e-mail e a senha que você criou.</p>

        <label className="en-lbl en-lbl-top" htmlFor="email">
          Seu e-mail
        </label>
        <input
          id="email"
          className="en-in"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoFocus
          aria-invalid={error ? true : undefined}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@email.com"
        />

        <label className="en-lbl en-lbl-top" htmlFor="senha">
          Sua senha
        </label>
        <input
          id="senha"
          className="en-in"
          type="password"
          autoComplete="current-password"
          aria-invalid={error ? true : undefined}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          placeholder="••••••••••"
        />

        {error && (
          <p className="en-error" role="alert">
            {error}
          </p>
        )}

        <button className={`btn btn--primary ${desktop ? 'en-dk-cta' : 'en-cta'}`} disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>

        <Link className="en-tbtn" to="/entrar/codigo">
          Esqueci minha senha
        </Link>

        {googleEnabled() && (
          <>
            <div className="en-ou" role="separator">
              <span>ou</span>
            </div>
            <GoogleButton label="Entrar com o Google" />
          </>
        )}

        <p className="en-note">
          <IconMail />
          <span>
            Ainda não tem conta? <Link to="/cadastro">Criar conta grátis</Link>
          </span>
        </p>
      </div>
    </form>
  );

  if (desktop) return <EntrarShell>{form}</EntrarShell>;
  return (
    <main className="en-page">
      <header className="en-bar" style={{ paddingLeft: 20 }}>
        <Brand />
      </header>
      {form}
    </main>
  );
}
